/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstanceRegion } from "../../../config/axios-region";
import {
  ClinicianStudySearchParams,
  ClinicianStudyAccessGrantSearchParams,
  StudyBreakGlassSearchRequest,
  DownloadStudyRequestV1,
} from "../../../types/study";
import { StudyAction } from "./studySlice";
import { AppThunk } from "../../../config/store";
import {
  IStudyDetail,
  SlicePaginationResponse,
  convertDateToStringRequest,
  initSlicePageable,
  IDiagnosticReportContent,
  yyyyMMdd,
  IBreakGlassPatientResponse,
  formatDate,
  ContentType,
  DEFAULT_TIMEZONE,
  prodModeLog,
  getBlobUrlForPDFPreview,
  expandHtmlPlainTextCompatibleContents,
  isViewAsTextContent,
} from "@advapacs/common";
import { StudyApi, authenticatedUserApi } from "../../../config/api";
import axios, { Axios, AxiosResponse } from "axios";
import {
  AdvapacsViewerToken,
  ClinicianEunityResponse,
  MeddreamToken,
} from "../../../types/integrations";
import { endOfDay, startOfDay } from "date-fns";

export const getStudies =
  (item: ClinicianStudySearchParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(StudyAction.getStudiesStart());
      const {
        page,
        size = initSlicePageable.size,
        patientName,
        patientId,
        accessionNumber,
        patientDob,
        description,
        modalitiesIds,
        studyDateStart,
        studyDateEnd,
        studyReceivedDate,
        storageClasses,
        studyReceivedDateStart,
        studyReceivedDateEnd,
        customerIds,
      } = item;
      let requestParams = {};
      if (patientName) {
        requestParams = {
          ...requestParams,
          patientName: patientName.trim(),
        };
      }
      if (studyReceivedDate) {
        requestParams = {
          ...requestParams,
          studyReceivedDate,
        };
      }
      if (studyDateStart) {
        requestParams = {
          ...requestParams,
          studyDateStart: convertDateToStringRequest(
            startOfDay(studyDateStart)
          ),
        };
      }
      if (studyDateEnd) {
        requestParams = {
          ...requestParams,
          studyDateEnd: convertDateToStringRequest(endOfDay(studyDateEnd)),
        };
      }
      if (patientId) {
        requestParams = {
          ...requestParams,
          patientId: patientId.trim(),
        };
      }
      if (accessionNumber) {
        requestParams = {
          ...requestParams,
          accessionNumber,
        };
      }
      if (patientDob) {
        requestParams = {
          ...requestParams,
          patientDob,
        };
      }
      if (description) {
        requestParams = {
          ...requestParams,
          description: description.trim(),
        };
      }
      if (modalitiesIds && modalitiesIds.length > 0) {
        requestParams = {
          ...requestParams,
          modalities: modalitiesIds.toString(),
        };
      }

      if (storageClasses && storageClasses.length > 0) {
        requestParams = {
          ...requestParams,
          storageClasses: storageClasses.toString(),
        };
      }
      if (customerIds && customerIds.length > 0) {
        requestParams = {
          ...requestParams,
          clinicianPortalIds: customerIds.toString(),
        };
      }
      if (studyReceivedDateStart) {
        requestParams = {
          ...requestParams,
          studyReceivedDateStart: convertDateToStringRequest(
            studyReceivedDateStart
          ),
        };
      }
      if (studyReceivedDateEnd) {
        requestParams = {
          ...requestParams,
          studyReceivedDateEnd:
            convertDateToStringRequest(studyReceivedDateEnd),
        };
      }
      if (page) requestParams = { ...requestParams, page };
      if (size) requestParams = { ...requestParams, size };

      const studies = await axiosInstanceRegion.get(StudyApi.GET_STUDIES, {
        params: requestParams,
      });
      dispatch(
        StudyAction.getStudiesSuccess(
          studies.data as SlicePaginationResponse<IStudyDetail>
        )
      );
    } catch (err) {
      dispatch(StudyAction.getStudiesFail(err?.response?.data));
    }
  };

export const getBreakGlassPatients =
  (item: ClinicianStudyAccessGrantSearchParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(StudyAction.getBreakGlassPatientsStart());
      const {
        page,
        size = initSlicePageable.size,
        patientName,
        patientId,
        patientDob,
        filterType,
      } = item;
      const requestSearch: StudyBreakGlassSearchRequest = {
        patientId: filterType === "id" ? "" : undefined,
        patientNameAndDob:
          filterType === "name"
            ? {
                patientDob: "",
                patientName: "",
              }
            : undefined,
      };
      if (patientName && filterType === "name") {
        requestSearch.patientNameAndDob.patientName = patientName;
      }
      if (patientId && filterType === "id") {
        requestSearch.patientId = patientId.trim();
      }
      if (patientDob && filterType === "name") {
        requestSearch.patientNameAndDob.patientDob = formatDate(
          patientDob as Date,
          yyyyMMdd
        );
      }
      const studies = await axiosInstanceRegion.post(
        `${StudyApi.GET_BREAK_GLASS}?page=${page ? page : 0}&size=${
          size ? size : 10
        }`,
        requestSearch
      );
      dispatch(
        StudyAction.getBreakGlassPatientsSuccess(
          studies.data as SlicePaginationResponse<IBreakGlassPatientResponse>
        )
      );
    } catch (err) {
      dispatch(StudyAction.getBreakGlassPatientsFail(err?.response?.data));
    }
  };

export const getDiagnosticReport =
  (portalId: string, patientId: string, studyId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(StudyAction.getReportStart());
      const response = await axiosInstanceRegion.get(
        StudyApi.DIAGNOSTIC_REPORT(portalId, patientId, studyId)
      );
      response.data.contents = expandHtmlPlainTextCompatibleContents(
        response.data.contents
      );
      dispatch(StudyAction.getReportSuccess(response.data));
    } catch (err) {
      dispatch(StudyAction.getReportFail());
    }
  };

export const fetchReportDataContent = async (
  portalId: string,
  patientId: string,
  studyId: string,
  selectContent: IDiagnosticReportContent,
  report: boolean
): Promise<string> => {
  let result = "";
  if (selectContent.contentType === ContentType.APPLICATION_PDF) {
    const hasPrams = !(!report && selectContent?.tag === ContentType.PDF_TAG);
    const path = !report
      ? StudyApi.GET_DIAGNOSTIC_REPORT_CONTENT(
          portalId,
          patientId,
          studyId,
          selectContent.id
        )
      : StudyApi.GENERATE_PDF_REPORT(
          portalId,
          patientId,
          studyId,
          selectContent.id
        );

    result = await getBlobUrlForPDFPreview(
      hasPrams,
      path,
      axiosInstanceRegion.defaults.baseURL
    );
  } else {
    const viewAsText = isViewAsTextContent(selectContent);
    const response = await axiosInstanceRegion.get(
      StudyApi.GET_DIAGNOSTIC_REPORT_CONTENT(
        portalId,
        patientId,
        studyId,
        selectContent.id
      ),
      {
        params: viewAsText ? { viewAsText: true } : undefined,
      }
    );
    result = response.data;
  }
  return result;
};

export const getStudyEUnityToken = async (
  studyIds: Array<string>
): Promise<AxiosResponse<ClinicianEunityResponse>> => {
  return axiosInstanceRegion.post(StudyApi.GET_EUNITY_TOKEN, { studyIds });
};

export const getStudyMeddreamToken = async (
  studyIds: Array<string>
): Promise<AxiosResponse<MeddreamToken>> => {
  return axiosInstanceRegion.post(StudyApi.GET_MEDDREAM_TOKEN, {
    studyIds,
  });
};

export const getStudyAdvapacsViewerToken = async (
  studyIds: Array<string>
): Promise<AxiosResponse<AdvapacsViewerToken>> => {
  return axiosInstanceRegion.post(StudyApi.GET_ADVAPACS_VIEWER_TOKEN, {
    studyIds,
  });
};
export const downloadStudiesV1 =
  (data: DownloadStudyRequestV1): AppThunk =>
  async () => {
    try {
      const {
        anonymize,
        mode,
        studyIds,
        portalIds,
        decompress,
        includeReport,
      } = data;
      const region = localStorage.getItem("region") || "";
      const urlRefresh = region
        ? `${authenticatedUserApi.REFRESH_TOKEN}?region=${region}`
        : authenticatedUserApi.REFRESH_TOKEN;
      await axios.post(urlRefresh);

      let params: Record<string, any> = {
        origin: window.origin,
        studyIds: Array.from(new Set(studyIds)),
        portalIds: portalIds, // don't need to set unique for this values.
        includeReport: includeReport,
        "x-tz": DEFAULT_TIMEZONE.value,
      };

      if (decompress != undefined) {
        params = {
          ...params,
          uncompressed: decompress,
        };
      }

      if (anonymize) {
        params = {
          ...params,
          anonymize: JSON.stringify(anonymize),
        };
      }
      if (mode) {
        params = {
          ...params,
          mode: mode,
        };
      }
      const urlParams = new URLSearchParams(params);
      const SERVER_API_URL = axiosInstanceRegion.defaults.baseURL;

      const finalUrl = `${SERVER_API_URL}${
        StudyApi.DOWNLOAD_STUDIES
      }?${urlParams.toString()}`;

      prodModeLog("Download final url: " + finalUrl); // log prod.

      window.open(finalUrl, "_blank");
    } catch (err) {}
  };
export const downloadPdf = async (
  portalId: string,
  patientId: string,
  studyId: string,
  content: IDiagnosticReportContent,
  report: boolean
) => {
  const contentId = content?.id;
  const url = !report
    ? StudyApi.GET_DIAGNOSTIC_REPORT_CONTENT(
        portalId,
        patientId,
        studyId,
        contentId
      )
    : StudyApi.GENERATE_PDF_REPORT(portalId, patientId, studyId, contentId);
  const params: Record<string, string> = {
    origin: window.origin,
    download: "true",
    "x-tz": DEFAULT_TIMEZONE.value,
  };
  const viewAsText = isViewAsTextContent(content);
  if (!report && viewAsText) {
    params.viewAsText = "true";
  }
  const urlParams = new URLSearchParams(params);
  const SERVER_API_URL = axiosInstanceRegion.defaults.baseURL;
  window.open(`${SERVER_API_URL}${url}?${urlParams.toString()}`, "_blank");
};

export const createHttpRequestAsyncThunk = (
  actionType: string,
  apiUrl: string,
  config?: {
    getAxiosInstance?: () => Axios;
    method?: string;
  }
) =>
  createAsyncThunk(
    actionType,
    async (
      data: {
        payload: any;
        pathVariables: { [key: string]: string };
      },
      { rejectWithValue }
    ) => {
      try {
        if ((data as unknown as any)?.pathVariables) {
          const pathVariables = (data as unknown as any)?.pathVariables;
          Object.keys(pathVariables).forEach((key) => {
            apiUrl = apiUrl.replace(`{${key}}`, pathVariables[key]);
          });
          data = (data as any).payload;
        }
        const response = await (config?.getAxiosInstance
          ? config?.getAxiosInstance()
          : axios
        ).request({
          url: apiUrl,
          method: config?.method || "GET",
          data,
        });
        return response.data;
      } catch (err) {
        return rejectWithValue(err?.response?.data);
      }
    }
  );
